import React, { useState, useRef, useEffect, useLayoutEffect, useCallback } from 'react';

const CLASS_PREFIX = 'mission-card';

export function getCardNameEditMeta(task) {
  const isDecision = task?.nodeType === 'decision';
  const isMilestone = task?.nodeType === 'milestone';
  const isWait = task?.nodeType === 'wait';
  const isPlaceholderName =
    !task?.name
    || task.name === 'ENTER MISSION NAME'
    || task.name === 'New Decision'
    || (isMilestone && task.name === 'Milestone');

  const emptyDisplayLabel = isDecision
    ? 'Untitled decision'
    : isWait
      ? 'Untitled wait'
      : isMilestone
        ? 'Untitled milestone'
        : 'Untitled mission';

  const placeholderHint = isDecision
    ? 'Type a decision question...'
    : isWait
      ? 'What are you waiting for?'
      : isMilestone
        ? 'Type a milestone name...'
        : 'Type a mission name...';

  return { isPlaceholderName, emptyDisplayLabel, placeholderHint };
}

/**
 * Inline editable mission/decision/milestone name — shared by CardHeader and MilestoneNode.
 * Always renders a textarea (display + edit) to avoid layout shift on toggle.
 */
const EditableCardName = ({
  task,
  onRename,
  onFinishTitleEdit,
  isEditingTitle = false,
  autoFocusOnPlaceholder = true,
  className = '',
  inputClassName = '',
}) => {
  const { isPlaceholderName, emptyDisplayLabel, placeholderHint } = getCardNameEditMeta(task);
  const displayValue = task.name || emptyDisplayLabel;

  const [isLocalEditing, setIsLocalEditing] = useState(false);
  const isEditingName = isEditingTitle || isLocalEditing;
  const [editValue, setEditValue] = useState(task.name);
  const nameInputRef = useRef(null);
  const clickCursorPosRef = useRef(null);
  const autoStartedForTaskRef = useRef(null);

  const collapseNameSelection = useCallback(() => {
    const el = nameInputRef.current;
    if (!el) return;
    try {
      el.setSelectionRange(0, 0);
    } catch {
      // ignore disconnected nodes
    }
  }, []);

  useEffect(() => {
    if (!isEditingName) {
      setEditValue(task.name);
    }
  }, [task.name, isEditingName]);

  useEffect(() => {
    if (
      autoFocusOnPlaceholder
      && isPlaceholderName
      && !isEditingName
      && autoStartedForTaskRef.current !== task.id
    ) {
      autoStartedForTaskRef.current = task.id;
      setEditValue('');
      setIsLocalEditing(true);
    }
  }, [task.id, isPlaceholderName, isEditingName, autoFocusOnPlaceholder]);

  useEffect(() => {
    if (!isEditingName) return undefined;

    const rafId = requestAnimationFrame(() => {
      if (nameInputRef.current) {
        nameInputRef.current.focus();
        const len = nameInputRef.current.value.length;
        if (clickCursorPosRef.current !== null) {
          const pos = clickCursorPosRef.current;
          nameInputRef.current.setSelectionRange(pos, pos);
        } else {
          nameInputRef.current.setSelectionRange(len, len);
        }
        clickCursorPosRef.current = null;
      }
    });
    return () => cancelAnimationFrame(rafId);
  }, [isEditingName]);

  // After React commits display mode, drop focus and any Ctrl+A highlight.
  useLayoutEffect(() => {
    if (!isEditingName) {
      collapseNameSelection();
      if (document.activeElement === nameInputRef.current) {
        nameInputRef.current.blur();
      }
    }
  }, [isEditingName, displayValue, collapseNameSelection]);

  // Capture mousedown outside so selection clears before canvas handlers run.
  useEffect(() => {
    const onDocMouseDown = (event) => {
      const el = nameInputRef.current;
      if (!el || el.contains(event.target)) return;

      const isActive = document.activeElement === el;
      const hasRange = el.selectionStart !== el.selectionEnd;
      if (!isActive && !hasRange) return;

      collapseNameSelection();
    };

    document.addEventListener('mousedown', onDocMouseDown, true);
    return () => document.removeEventListener('mousedown', onDocMouseDown, true);
  }, [task.id, collapseNameSelection]);

  const handleNameClick = (e) => {
    e.stopPropagation();

    if (isPlaceholderName) {
      setEditValue('');
      clickCursorPosRef.current = null;
    } else if (nameInputRef.current) {
      clickCursorPosRef.current = nameInputRef.current.selectionStart;
    } else {
      clickCursorPosRef.current = editValue.length;
    }

    setIsLocalEditing(true);
  };

  const handleNameSave = () => {
    collapseNameSelection();
    const trimmedValue = editValue.trim();
    if (trimmedValue !== task.name) {
      onRename?.(task.id, trimmedValue);
    }
    setEditValue(trimmedValue);
    setIsLocalEditing(false);
    onFinishTitleEdit?.();
  };

  // Leave edit mode by blurring — onBlur runs handleNameSave. Avoid calling
  // handleNameSave then blur in the same tick (stale onBlur would double-save).
  const handleNameKeyDown = (e) => {
    e.stopPropagation();
    if (e.key === 'Enter') {
      e.preventDefault();
      // Blur commits via onBlur (same pattern as duration fields). Then notify
      // hosts (e.g. Flow) so they can clear card selection.
      e.target.blur();
      window.dispatchEvent(new CustomEvent('missionCardTitleEnter', { detail: { taskId: task.id } }));
    } else if (e.key === 'Escape') {
      e.preventDefault();
      collapseNameSelection();
      setEditValue(task.name);
      setIsLocalEditing(false);
      onFinishTitleEdit?.();
      // Focus is cleared in the display-mode layout effect after onBlur is detached.
    }
  };

  return (
    <div className={`${CLASS_PREFIX}__name-container ${className}`.trim()}>
      {isEditingName && editValue === '' && (
        <span className={`${CLASS_PREFIX}__name-hint`}>💡 {placeholderHint}</span>
      )}
      <textarea
        ref={nameInputRef}
        className={[
          `${CLASS_PREFIX}__name-input`,
          !isEditingName && `${CLASS_PREFIX}__name-input--display`,
          !isEditingName && isPlaceholderName && `${CLASS_PREFIX}__name-input--placeholder`,
          inputClassName,
        ].filter(Boolean).join(' ')}
        value={isEditingName ? editValue : displayValue}
        onChange={(e) => setEditValue(e.target.value)}
        onBlur={isEditingName ? handleNameSave : undefined}
        onKeyDown={isEditingName ? handleNameKeyDown : undefined}
        onClick={(e) => {
          e.stopPropagation();
          if (!isEditingName) handleNameClick(e);
        }}
        onMouseDown={(e) => e.stopPropagation()}
        readOnly={!isEditingName}
        tabIndex={isEditingName ? 0 : -1}
        rows={1}
      />
    </div>
  );
};

export default EditableCardName;
